import React, { useState } from 'react';
import { Copy, Download, Check, Edit2, Save, X } from 'lucide-react';
import { SavedServer } from '../types';
import api from '../api/client';
import './JsonViewer.css';

interface JsonViewerProps {
  server: SavedServer;
  onSave?: (server: SavedServer) => void;
}

const JsonViewer: React.FC<JsonViewerProps> = ({ server, onSave }) => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'server' | 'season' | 'merged'>('server');
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Extract individual configs from the server object
  const serverConfig = {
    server_tcp_listener_port: server.serverConfig.server_tcp_listener_port,
    server_udp_listener_port: server.serverConfig.server_udp_listener_port,
    server_tcp_internal_port: server.serverConfig.server_tcp_internal_port,
    server_udp_internal_port: server.serverConfig.server_udp_internal_port,
    server_http_port: server.serverConfig.server_http_port,
    server_name: server.serverConfig.server_name,
    max_players: server.serverConfig.max_players,
    cycle: server.serverConfig.cycle,
    allowed_cars_list_full: server.serverConfig.allowed_cars_list_full,
    driver_password: server.serverConfig.driver_password,
    spectator_password: server.serverConfig.spectator_password,
    admin_password: server.serverConfig.admin_password,
    type: server.serverConfig.type,
    entry_list_path: server.serverConfig.entry_list_path,
    results_path: server.serverConfig.results_path,
  };

  const seasonConfig = {
    game_type: server.seasonConfig.game_type,
    event: server.seasonConfig.event,
    export_json: server.seasonConfig.export_json,
    game_config: server.seasonConfig.game_config,
    weather_type: server.seasonConfig.weather_type,
    weather_behaviour: server.seasonConfig.weather_behaviour,
    initial_grip: server.seasonConfig.initial_grip,
  };

  let displayJson = '';
  if (activeTab === 'server') {
    displayJson = JSON.stringify(serverConfig, null, 2);
  } else if (activeTab === 'season') {
    displayJson = JSON.stringify(seasonConfig, null, 2);
  } else {
    displayJson = JSON.stringify(server, null, 2);
  }

  const handleCopyJson = () => {
    navigator.clipboard.writeText(displayJson);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadJson = () => {
    const element = document.createElement('a');
    const file = new Blob([displayJson], { type: 'application/json' });
    element.href = URL.createObjectURL(file);
    const filename = activeTab === 'server' ? 'server.json' : activeTab === 'season' ? 'season.json' : `${server.name}-config.json`;
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleEdit = () => {
    if (activeTab === 'merged') {
      setError('Cannot edit merged config. Please edit server.json or season.json individually.');
      return;
    }
    setIsEditing(true);
    setEditText(displayJson);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditText('');
    setError(null);
  };

  const handleSave = async () => {
    try {
      setError(null);
      setIsSaving(true);

      // Validate JSON syntax
      let parsedJson;
      try {
        parsedJson = JSON.parse(editText);
      } catch (e) {
        setError(`Invalid JSON: ${(e as Error).message}`);
        return;
      }

      // Save based on active tab
      let response;
      if (activeTab === 'server') {
        response = await api.saveServerJson(server.id, parsedJson);
      } else if (activeTab === 'season') {
        response = await api.saveSeasonJson(server.id, parsedJson);
      }

      setSuccess(`${activeTab === 'server' ? 'server.json' : 'season.json'} saved successfully!`);
      setIsEditing(false);
      setEditText('');

      // Call onSave to trigger reload of server data
      if (onSave && response?.data) {
        onSave(response.data);
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to save: ${(err as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="json-viewer">
      <div className="json-tabs">
        <button
          className={`json-tab ${activeTab === 'server' ? 'active' : ''}`}
          onClick={() => {
            if (!isEditing) setActiveTab('server');
          }}
          disabled={isEditing}
        >
          server.json
        </button>
        <button
          className={`json-tab ${activeTab === 'season' ? 'active' : ''}`}
          onClick={() => {
            if (!isEditing) setActiveTab('season');
          }}
          disabled={isEditing}
        >
          season.json
        </button>
        <button
          className={`json-tab ${activeTab === 'merged' ? 'active' : ''}`}
          onClick={() => {
            if (!isEditing) setActiveTab('merged');
          }}
          disabled={isEditing}
        >
          Merged Config
        </button>
      </div>
      <div className="json-toolbar">
        {!isEditing ? (
          <>
            <button
              className="json-button copy"
              onClick={handleCopyJson}
              title="Copy JSON to clipboard"
            >
              {copied ? (
                <>
                  <Check size={16} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={16} />
                  Copy JSON
                </>
              )}
            </button>
            <button
              className="json-button download"
              onClick={handleDownloadJson}
              title="Download JSON file"
            >
              <Download size={16} />
              Download
            </button>
            <button
              className="json-button edit"
              onClick={handleEdit}
              title="Edit JSON"
              disabled={activeTab === 'merged'}
            >
              <Edit2 size={16} />
              Edit
            </button>
          </>
        ) : (
          <>
            <button
              className="json-button save"
              onClick={handleSave}
              disabled={isSaving}
              title="Save changes"
            >
              <Save size={16} />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              className="json-button cancel"
              onClick={handleCancel}
              disabled={isSaving}
              title="Cancel editing"
            >
              <X size={16} />
              Cancel
            </button>
          </>
        )}
      </div>

      {error && <div className="json-error">{error}</div>}
      {success && <div className="json-success">{success}</div>}

      {isEditing ? (
        <textarea
          className="json-editor"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          placeholder="Enter valid JSON..."
        />
      ) : (
        <pre className="json-content">{displayJson}</pre>
      )}
    </div>
  );
};

export default JsonViewer;
